﻿import { hasDataPage, qs } from "../core/dom";
import { highlightCode, preloadShiki } from "../lib/shiki-highlighter";

let htmlRenderVersion = 0;

export async function initHtmlEditorPage() {
    if (!hasDataPage("html-editor")) return;

    const page = {
        htmlEditor: qs("[data-html-editor]"),
        htmlInput: qs("[data-html-input]"),
        htmlHighlight: qs("[data-html-highlight]"),
        htmlPreview: qs("[data-html-preview]"),

        autoPreview: qs("[data-auto-preview]"),
        runPreviewBtn: qs("[data-run-preview]"),
        copyHtmlBtn: qs("[data-copy-html]"),
        downloadHtmlBtn: qs("[data-download-html]"),
        loadExampleBtn: qs("[data-load-example]"),
        clearHtmlBtn: qs("[data-clear-html]"),
        formatHtmlBtn: qs("[data-format-html]"),
        minifyHtmlBtn: qs("[data-minify-html]"),
        validateHtmlBtn: qs("[data-validate-html]"),
        htmlStatus: qs("[data-html-status]"),
        htmlResultsSummary: qs("[data-html-results-summary]"),
        htmlResultsList: qs("[data-html-results-list]"),

        rightTabs: Array.from(document.querySelectorAll("[data-html-right-tab]")),
        rightViews: Array.from(document.querySelectorAll("[data-html-right-view]")),
    };

    bindEvents(page);

    await preloadShiki();
    await renderInitialState(page);
}

function bindRightTabs(page) {
    page.rightTabs.forEach(tab => {
        tab.addEventListener("click", () => {
            const target = tab.dataset.htmlRightTab;

            page.rightTabs.forEach(x => x.classList.toggle("active", x === tab));
            page.rightViews.forEach(view => {
                const isActive = view.dataset.htmlRightView === target;
                view.hidden = !isActive;
                view.classList.toggle("active", isActive);
            });
        });
    });
}

async function formatHtml(page) {
    const raw = page.htmlInput?.value?.trim();
    if (!raw) return;

    const formatted = prettyPrintHtml(raw);
    page.htmlInput.value = formatted;

    autoResizeEditor(page);
    await renderHtmlInput(page);

    if (page.autoPreview?.checked) {
        renderPreview(page);
    }

    setHtmlStatus(page, "Formatted", "is-valid");
    renderHtmlResults(page, [{
        type: "success",
        title: "HTML formatted",
        message: "The HTML document has been formatted successfully."
    }], "Formatting completed successfully.");
}

async function minifyHtml(page) {
    const raw = page.htmlInput?.value?.trim();
    if (!raw) return;

    const minified = raw
        .replace(/>\s+</g, "><")
        .replace(/\n+/g, "")
        .replace(/\s{2,}/g, " ")
        .trim();

    page.htmlInput.value = minified;

    autoResizeEditor(page);
    await renderHtmlInput(page);

    if (page.autoPreview?.checked) {
        renderPreview(page);
    }

    setHtmlStatus(page, "Minified", "is-valid");
    renderHtmlResults(page, [{
        type: "success",
        title: "HTML minified",
        message: "Whitespace between tags has been reduced."
    }], "Minification completed successfully.");
}

function validateHtml(page) {
    const raw = page.htmlInput?.value?.trim();
    if (!raw) {
        renderHtmlResults(page, [], "No HTML content to validate.");
        setHtmlStatus(page, "Empty", "");
        return;
    }

    const messages = [];
    const stack = [];
    const voidTags = new Set([
        "area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta", "param", "source", "track", "wbr"
    ]);

    const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9-]*)([^>]*)>/g;
    let match;

    while ((match = tagRegex.exec(raw)) !== null) {
        const full = match[0];
        const tagName = match[1].toLowerCase();
        const attrs = match[2] || "";

        const isClosing = full.startsWith("</");
        const isSelfClosing = /\/>$/.test(full) || voidTags.has(tagName);

        if (!isClosing && !isSelfClosing) {
            stack.push(tagName);
        }

        if (isClosing) {
            const last = stack.pop();
            if (last !== tagName) {
                messages.push({
                    type: "error",
                    title: `Tag mismatch`,
                    message: `Expected closing tag for <${last || "unknown"}> but found </${tagName}>.`
                });
            }
        }

        if (tagName === "img" && !/\balt\s*=/i.test(attrs)) {
            messages.push({
                type: "warning",
                title: "Missing alt attribute",
                message: "<img> elements should usually include an alt attribute."
            });
        }
    }

    if (stack.length) {
        stack.reverse().forEach(tag => {
            messages.push({
                type: "error",
                title: "Unclosed tag",
                message: `<${tag}> does not appear to have a matching closing tag.`
            });
        });
    }

    if (!/<!doctype html>/i.test(raw)) {
        messages.push({
            type: "warning",
            title: "Missing DOCTYPE",
            message: "A modern HTML document should usually start with <!DOCTYPE html>."
        });
    }

    if (!/<html[^>]*\blang\s*=/i.test(raw)) {
        messages.push({
            type: "warning",
            title: "Missing lang attribute",
            message: "The <html> element should usually define a lang attribute."
        });
    }

    const hasErrors = messages.some(x => x.type === "error");

    if (!messages.length) {
        messages.push({
            type: "success",
            title: "No issues found",
            message: "No basic structural issues were detected."
        });
    }

    renderHtmlResults(
        page,
        messages,
        hasErrors
            ? "Validation completed with errors."
            : "Validation completed successfully."
    );

    setHtmlStatus(page, hasErrors ? "Invalid" : "Valid", hasErrors ? "is-error" : "is-valid");
}

function renderHtmlResults(page, messages, summaryText) {
    if (page.htmlResultsSummary) {
        page.htmlResultsSummary.textContent = summaryText;
    }

    if (!page.htmlResultsList) return;

    if (!messages.length) {
        page.htmlResultsList.innerHTML = `
            <div class="html-result-empty">
                <i class="fa-regular fa-circle-info"></i>
                <span>No messages.</span>
            </div>
        `;
        return;
    }

    page.htmlResultsList.innerHTML = messages.map(item => `
        <article class="html-result-card is-${item.type}">
            <div class="html-result-card__icon">
                <i class="fa-regular ${resolveResultIcon(item.type)}"></i>
            </div>
            <div class="html-result-card__content">
                <h4>${escapeHtml(item.title)}</h4>
                <p>${escapeHtml(item.message)}</p>
            </div>
        </article>
    `).join("");
}

function resolveResultIcon(type) {
    switch (type) {
        case "error": return "fa-circle-exclamation";
        case "warning": return "fa-triangle-exclamation";
        default: return "fa-circle-check";
    }
}

function setHtmlStatus(page, text, stateClass = "") {
    if (!page.htmlStatus) return;

    page.htmlStatus.textContent = text;
    page.htmlStatus.classList.remove("is-valid", "is-error");

    if (stateClass) {
        page.htmlStatus.classList.add(stateClass);
    }
}

function prettyPrintHtml(html) {
    const voidTags = new Set([
        "area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta", "param", "source", "track", "wbr"
    ]);

    const tokens = html
        .replace(/>\s+</g, "><")
        .replace(/</g, "\n<")
        .replace(/>/g, ">\n")
        .split("\n")
        .map(x => x.trim())
        .filter(Boolean);

    let indent = 0;
    const out = [];

    for (const token of tokens) {
        const closingMatch = token.match(/^<\/([a-zA-Z0-9-]+)/);
        const openingMatch = token.match(/^<([a-zA-Z0-9-]+)/);

        if (closingMatch) {
            indent = Math.max(indent - 1, 0);
        }

        out.push(`${"    ".repeat(indent)}${token}`);

        if (openingMatch) {
            const tag = openingMatch[1].toLowerCase();
            const isClosing = /^<\//.test(token);
            const isSelfClosing = /\/>$/.test(token) || voidTags.has(tag);

            if (!isClosing && !isSelfClosing && !token.includes(`</${tag}>`)) {
                indent++;
            }
        }
    }

    return out.join("\n");
}

function escapeHtml(value) {
    return String(value)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function bindEvents(page) {

    page.htmlInput?.addEventListener("input", debounce(async () => {
        autoResizeEditor(page);
        await renderHtmlInput(page);

        if (page.autoPreview?.checked) {
            renderPreview(page);
        }
    }, 120));

    page.htmlInput?.addEventListener("keydown", async (e) => {
        const handled = handleHtmlEditorKeydown(e, page);
        if (handled) {
            //autoResizeEditor(page);
            await renderHtmlInput(page);

            if (page.autoPreview?.checked) {
                renderPreview(page);
            }
        }
    });

    page.htmlInput?.addEventListener("scroll", () => syncEditorScroll(page));
    page.htmlInput?.addEventListener("keydown", (e) => handleEditorTab(e));

    page.runPreviewBtn?.addEventListener("click", () => renderPreview(page));
    page.copyHtmlBtn?.addEventListener("click", async () => copyHtml(page));
    page.downloadHtmlBtn?.addEventListener("click", () => downloadHtml(page));
    page.loadExampleBtn?.addEventListener("click", async () => loadExample(page));
    page.clearHtmlBtn?.addEventListener("click", async () => clearHtml(page));

    document.addEventListener("click", (e) => {
        const toggle = e.target.closest("[data-theme-toggle]");
        if (!toggle) return;

        setTimeout(async () => {
            await renderHtmlInput(page);
        }, 60);
    });

    bindRightTabs(page);

    page.formatHtmlBtn?.addEventListener("click", async () => formatHtml(page));
    page.minifyHtmlBtn?.addEventListener("click", async () => minifyHtml(page));
    page.validateHtmlBtn?.addEventListener("click", () => validateHtml(page));
}

const VOID_HTML_TAGS = new Set([
    "area", "base", "br", "col", "embed", "hr", "img", "input",
    "link", "meta", "param", "source", "track", "wbr"
]);

function handleHtmlEditorKeydown(e, page) {
    const textarea = page.htmlInput;
    if (!textarea) return false;

    if (e.key === "Tab") {
        e.preventDefault();
        insertAtSelection(textarea, "    ");
        return true;
    }

    if (e.key === "Enter") {
        e.preventDefault();
        handleHtmlEnter(textarea);
        return true;
    }

    if (e.key === ">") {
        e.preventDefault();
        handleHtmlTagClose(textarea);
        return true;
    }

    return false;
}

function handleHtmlTagClose(textarea) {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;

    const before = value.slice(0, start);
    const after = value.slice(end);

    const selectedText = value.slice(start, end);
    const leftSide = before + selectedText;

    const openTagMatch = leftSide.match(/<([A-Za-z][A-Za-z0-9-]*)\b([^<>]*)$/);
    const closingTagMatch = leftSide.match(/<\/([A-Za-z][A-Za-z0-9-]*)$/);

    // Normal closing tag yazıyorsa sadece '>' ekle
    if (closingTagMatch) {
        textarea.value = before + ">" + after;
        textarea.selectionStart = textarea.selectionEnd = start + 1;
        return;
    }

    // Açılış tag ise otomatik kapat
    if (openTagMatch) {
        const tagName = openTagMatch[1].toLowerCase();
        const attrs = openTagMatch[2] || "";

        // self-closing ya da void tag ise sadece '>' koy
        if (VOID_HTML_TAGS.has(tagName) || /\/\s*$/.test(attrs)) {
            textarea.value = before + ">" + after;
            textarea.selectionStart = textarea.selectionEnd = start + 1;
            return;
        }

        const insertion = `></${tagName}>`;
        textarea.value = before + insertion + after;

        // caret açılış ve kapanış arasına gelsin
        const caretPos = start + 1;
        textarea.selectionStart = textarea.selectionEnd = caretPos;
        return;
    }

    // normal > yazımı
    textarea.value = before + ">" + after;
    textarea.selectionStart = textarea.selectionEnd = start + 1;
}

function handleHtmlEnter(textarea) {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;

    const before = value.slice(0, start);
    const after = value.slice(end);

    const currentLineStart = before.lastIndexOf("\n") + 1;
    const currentLine = before.slice(currentLineStart);
    const baseIndent = (currentLine.match(/^\s*/) || [""])[0];

    const beforeCaret = before;
    const afterCaret = after;

    // caret şu yapıdaysa: <div>|</div>
    const openTagMatch = beforeCaret.match(/<([A-Za-z][A-Za-z0-9-]*)\b[^<>]*>\s*$/);
    const closeTagMatch = afterCaret.match(/^\s*<\/([A-Za-z][A-Za-z0-9-]*)>/);

    if (
        openTagMatch &&
        closeTagMatch &&
        openTagMatch[1].toLowerCase() === closeTagMatch[1].toLowerCase()
    ) {
        const innerIndent = baseIndent + "    ";
        const insertion = `\n${innerIndent}\n${baseIndent}`;
        textarea.value = before + insertion + after;
        textarea.selectionStart = textarea.selectionEnd = start + innerIndent.length + 1;
        return;
    }

    // normal enter: indent koru
    const insertion = `\n${baseIndent}`;
    textarea.value = before + insertion + after;
    textarea.selectionStart = textarea.selectionEnd = start + insertion.length;
}

function insertAtSelection(textarea, text) {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;

    textarea.value = value.slice(0, start) + text + value.slice(end);
    textarea.selectionStart = textarea.selectionEnd = start + text.length;
}

async function renderInitialState(page) {
    autoResizeEditor(page);
    await renderHtmlInput(page);
    renderPreview(page);
}

async function renderHtmlInput(page) {
    if (!page.htmlHighlight || !page.htmlInput) return;

    const currentVersion = ++htmlRenderVersion;
    const code = page.htmlInput.value || " ";

    const html = await highlightCode(code, "html");

    if (currentVersion !== htmlRenderVersion) return;

    page.htmlHighlight.innerHTML = html;
    syncEditorScroll(page);
}

function renderPreview(page) {
    if (!page.htmlPreview) return;

    const rawHtml = page.htmlInput?.value?.trim();

    if (!rawHtml) {
        page.htmlPreview.srcdoc = getDefaultPreviewDocument();
        return;
    }

    page.htmlPreview.srcdoc = buildPreviewDocument(rawHtml);
}

function buildPreviewDocument(content) {
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>HTML Preview</title>

    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap" rel="stylesheet">

    <style>
        html, body {
            margin: 0;
            padding: 0;
            font-family: 'Open Sans', Arial, sans-serif;
        }

        body {
            min-height: 100vh;
        }
    </style>
</head>
<body>
${content}
</body>
</html>`;
}

function autoResizeEditor(page) {
    const textarea = page.htmlInput;
    const highlight = page.htmlHighlight;

    if (!textarea || !highlight) return;

    textarea.style.height = "auto";
    const newHeight = Math.max(textarea.scrollHeight, 520);

    textarea.style.height = `${newHeight}px`;
    highlight.style.height = `${newHeight}px`;
}

function autoResizeEditorNew(page) {
    const textarea = page.htmlInput;
    const highlight = page.htmlHighlight;

    if (!textarea || !highlight) return;

    const currentHeight = textarea.offsetHeight;
    const neededHeight = Math.max(textarea.scrollHeight, 520);

    if (neededHeight > currentHeight) {
        textarea.style.height = `${neededHeight}px`;
        highlight.style.height = `${neededHeight}px`;
    }
}

function syncEditorScroll(page) {
    const textarea = page.htmlInput;
    const highlightHost = page.htmlHighlight;

    if (!textarea || !highlightHost) return;

    highlightHost.scrollTop = textarea.scrollTop;
    highlightHost.scrollLeft = textarea.scrollLeft;
}

function handleEditorTab(e) {
    if (e.key !== "Tab") return;

    e.preventDefault();

    const textarea = e.currentTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;

    textarea.value = value.substring(0, start) + "    " + value.substring(end);
    textarea.selectionStart = textarea.selectionEnd = start + 4;

    textarea.dispatchEvent(new Event("input", { bubbles: true }));
}

async function copyHtml(page) {
    const text = page.htmlInput?.value || "";
    await navigator.clipboard.writeText(text);
}

function downloadHtml(page) {
    const content = page.htmlInput?.value || "";
    const blob = new Blob([content], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "document.html";
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(url), 500);
}

async function loadExample(page) {
    page.htmlInput.value = getDefaultPreviewDocument();
    autoResizeEditor(page);
    await renderHtmlInput(page);
    renderPreview(page);
}

async function clearHtml(page) {
    page.htmlInput.value = "";
    autoResizeEditor(page);
    await renderHtmlInput(page);
    renderPreview(page);
}

function getDefaultPreviewDocument() {
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>HTML Preview</title>

    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap" rel="stylesheet">

    <style>
        * {
            box-sizing: border-box;
        }

        html, body {
            margin: 0;
            padding: 0;
            font-family: 'Open Sans', Arial, sans-serif;
        }

        body {
            padding: 40px;
            background: #0b1020;
            color: #e5e7eb;
        }

        .card {
            max-width: 720px;
            margin: 0 auto;
            padding: 28px;
            border-radius: 22px;
            background: rgba(255,255,255,0.06);
            border: 1px solid rgba(255,255,255,0.12);
            box-shadow: 0 20px 60px rgba(0,0,0,0.25);
        }

        .eyebrow {
            display: inline-block;
            margin-bottom: 12px;
            padding: 6px 12px;
            border-radius: 999px;
            background: rgba(99,102,241,0.16);
            color: #c7d2fe;
            font-size: 12px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: .08em;
        }

        h1 {
            margin: 0 0 12px;
            font-size: 40px;
            line-height: 1.1;
        }

        p {
            margin: 0 0 20px;
            color: #cbd5e1;
            line-height: 1.7;
        }

        .actions {
            display: flex;
            gap: 12px;
            flex-wrap: wrap;
        }

        .btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 42px;
            padding: 0 16px;
            border-radius: 12px;
            text-decoration: none;
            font-weight: 600;
        }

        .btn-primary {
            background: #6366f1;
            color: #fff;
        }

        .btn-secondary {
            background: rgba(255,255,255,0.08);
            color: #fff;
            border: 1px solid rgba(255,255,255,0.12);
        }
    </style>
</head>
<body>
    <div class="card">
        <span class="eyebrow">HTML Editor</span>
        <h1>Live HTML preview</h1>
        <p>
            Edit your HTML on the left and see the rendered result instantly on the right.
        </p>

        <div class="actions">
            <a href="#" class="btn btn-primary">Primary Action</a>
            <a href="#" class="btn btn-secondary">Secondary Action</a>
        </div>
    </div>
</body>
</html>`;
}

function debounce(fn, delay) {
    let timer = null;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
}